import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, CreditCard, Lock, Send, ShieldCheck, UserRound } from 'lucide-react';
import { getMemberByUser } from '@/lib/memberLookup';
import { deliverMemberNotification } from '@/lib/notificationDelivery';
import { toast } from 'sonner';

const LOGO_FALLBACK = '/mbv-logo-512.png';
const CARD_FONT_FAMILY = '"Montserrat", "Inter", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const CHURCH_CNPJ_FALLBACK = '24.862.609/0001-38';

function parseDateOnly(value) {
  if (!value) return null;
  const rawValue = String(value).trim();
  if (!rawValue) return null;

  const isoDate = rawValue.slice(0, 10);
  const isoMatch = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const brMatch = rawValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brMatch) {
    const [, day, month, year] = brMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsedDate = new Date(rawValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function formatDate(value) {
  const date = parseDateOnly(value);
  if (!date) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

function addOneYear(dateValue) {
  const baseDate = parseDateOnly(dateValue) || new Date();
  const nextYear = new Date(baseDate);
  nextYear.setFullYear(nextYear.getFullYear() + 1);
  return nextYear.toISOString().slice(0, 10);
}

function getRole(member) {
  return member?.member_card_role || member?.ministries?.[0] || 'Membro';
}

function getBirthDate(member) {
  return member?.birth_date || member?.birthdate || member?.date_of_birth || member?.data_nascimento || null;
}

function getCongregation(churchInfo) {
  return (
    churchInfo?.member_congregation ||
    churchInfo?.congregation_name ||
    churchInfo?.name ||
    'Assembleia de Deus MBV'
  );
}

function getChurchCnpj(churchInfo) {
  return churchInfo?.cnpj || churchInfo?.church_cnpj || churchInfo?.bank_document || CHURCH_CNPJ_FALLBACK;
}

function getMissingProfileFields(member) {
  const checks = [
    ['Foto de perfil', member?.photo_url],
    ['Nome completo', member?.full_name],
    ['Data de nascimento', member?.birth_date],
    ['Telefone', member?.phone],
    ['E-mail', member?.email],
    ['Data de batismo', member?.baptism_date],
    ['Data de membresia', member?.membership_date],
  ];

  return checks.filter(([, value]) => !String(value || '').trim()).map(([label]) => label);
}

function MemberCardFront({ member, churchInfo }) {
  const logoUrl = churchInfo?.logo_url || LOGO_FALLBACK;

  return (
    <div
      className="mbv-member-card-front relative mx-auto aspect-[0.63/1] w-full max-w-[330px] overflow-hidden rounded-[28px] p-6 text-white shadow-2xl"
      style={{ fontFamily: CARD_FONT_FAMILY }}
    >
      <div className="absolute inset-x-6 top-24 h-px bg-gradient-to-r from-transparent via-amber-200/50 to-transparent" />
      <div className="relative flex h-full flex-col items-center text-center">
        <div className="flex w-full items-start justify-between">
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-[0.24em] text-amber-200">Cartão de Membro</p>
            <h2 className="mt-1 max-w-[190px] text-sm font-semibold leading-tight text-white/90">
              {churchInfo?.name || 'Assembleia de Deus MBV'}
            </h2>
          </div>
          <Badge className="bg-emerald-400/20 text-emerald-100 border border-emerald-300/30 font-sans">Ativo</Badge>
        </div>

        <img src={logoUrl} alt="Logo da igreja" className="mt-4 h-24 w-24 object-contain drop-shadow-2xl" />

        <div className="mt-5 w-30 h-30 rounded-[24px] overflow-hidden bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0 shadow-xl" style={{ width: 120, height: 120 }}>
          {member.photo_url ? (
            <img src={member.photo_url} alt={member.full_name} className="w-full h-full object-cover" />
          ) : (
            <UserRound className="w-12 h-12 text-white/50" />
          )}
        </div>

        <div className="mt-4 min-w-0 w-full">
          <p className="text-[10px] uppercase tracking-[0.18em] text-white/55">Nome</p>
          <h1 className="mt-1 text-[1.45rem] font-extrabold leading-tight break-words">{member.full_name}</h1>
          <p className="mt-1 text-base font-semibold text-amber-100">{getRole(member)}</p>
        </div>

        <div className="mt-auto w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-center backdrop-blur-sm">
          <p className="text-[10px] uppercase tracking-[0.22em] text-white/60">Identificação digital</p>
        </div>
      </div>
    </div>
  );
}

function MemberCardBack({ member, churchInfo }) {
  const logoUrl = churchInfo?.footer_logo_url || churchInfo?.logo_url || LOGO_FALLBACK;
  const issuedAt = member.member_card_issued_at || new Date().toISOString().slice(0, 10);
  const expiresAt = member.member_card_expires_at || addOneYear(issuedAt);
  const birthDate = formatDate(getBirthDate(member));
  const congregation = member?.congregation || getCongregation(churchInfo);
  const churchCnpj = getChurchCnpj(churchInfo);

  return (
    <div
      className="mbv-member-card-back relative mx-auto aspect-[0.58/1] w-full max-w-[330px] overflow-hidden rounded-[28px] bg-white p-5 shadow-xl border border-slate-200"
      style={{ fontFamily: CARD_FONT_FAMILY }}
    >
      <div className="absolute inset-x-0 top-0 h-2 bg-[#d8bd68]" />
      <div className="h-full flex flex-col items-center text-center">
        <img src={logoUrl} alt="Logo da igreja" className="mt-1 h-16 w-16 object-contain" />
        <div className="mt-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Identificação Eclesiástica</p>
          <h2 className="mt-1 text-lg font-extrabold leading-tight text-slate-950">{churchInfo?.name || 'Assembleia de Deus MBV'}</h2>
        </div>

        <div className="mt-4 w-full space-y-2 text-left font-sans">
          <div className="rounded-xl bg-indigo-50 px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.12em] text-indigo-700">Congregação</p>
            <p className="text-sm font-bold leading-tight text-slate-950">{congregation}</p>
          </div>
          <div className="rounded-xl bg-indigo-50 px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.12em] text-indigo-700">CNPJ da igreja</p>
            <p className="text-sm font-bold leading-tight text-slate-950">{churchCnpj}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-slate-100 px-3 py-2">
              <p className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Nascimento</p>
              <p className="text-sm font-bold text-slate-950">{birthDate === '-' ? 'Não informada' : birthDate}</p>
            </div>
            <div className="rounded-xl bg-slate-100 px-3 py-2">
              <p className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Validade</p>
              <p className="text-sm font-bold text-slate-950">{formatDate(expiresAt)}</p>
            </div>
            <div className="rounded-xl bg-slate-100 px-3 py-2">
              <p className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Batismo</p>
              <p className="text-sm font-bold text-slate-950">{formatDate(member.baptism_date)}</p>
            </div>
            <div className="rounded-xl bg-slate-100 px-3 py-2">
              <p className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Membresia</p>
              <p className="text-sm font-bold text-slate-950">{formatDate(member.membership_date)}</p>
            </div>
            <div className="rounded-xl bg-slate-100 px-3 py-2">
              <p className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Emissão</p>
              <p className="text-sm font-bold text-slate-950">{formatDate(issuedAt)}</p>
            </div>
          </div>
        </div>

        <div className="mb-6 mt-auto w-full rounded-2xl bg-slate-100 px-3 py-2.5 text-[11px] leading-snug text-slate-700 font-sans">
          Este cartão é pessoal e deve ser apresentado junto a um documento oficial quando solicitado.
        </div>
      </div>
    </div>
  );
}
export default function CartaoMembro() {
  const queryClient = useQueryClient();
  const { data: currentUser, isLoading: loadingUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
    retry: false,
  });

  const { data: member, isLoading: loadingMember } = useQuery({
    queryKey: ['memberData', currentUser?.id, currentUser?.email],
    queryFn: () => getMemberByUser(currentUser),
    enabled: !!currentUser,
  });

  const { data: churchInfo } = useQuery({
    queryKey: ['churchInfo'],
    queryFn: async () => {
      const rows = await base44.entities.ChurchInfo.list();
      return rows?.[0] || null;
    },
  });

  const isLoading = loadingUser || loadingMember;
  const hasAccess = member?.status === 'Ativo' && member?.member_card_enabled === true;
  const missingFields = getMissingProfileFields(member);

  const requestAccessMutation = useMutation({
    mutationFn: async () => {
      if (!member?.id) throw new Error('Cadastro de membro não encontrado.');

      const nowIso = new Date().toISOString();
      const missingText = missingFields.length
        ? `Campos que precisam ser conferidos/preenchidos: ${missingFields.join(', ')}.`
        : 'Perfil informado como completo pelo membro.';
      const message =
        `${member.full_name} solicitou acesso ao Cartão de Membro.\n\n` +
        `${missingText}\n\n` +
        `E-mail: ${member.email || '-'}\nTelefone: ${member.phone || '-'}`;

      const permissions = await base44.entities.AdminPermission.list('-created_date', 2000).catch(() => []);
      const adminEmails = [
        ...new Set(
          permissions
            .filter((permission) => permission?.is_super_admin || permission?.can_manage_members)
            .map((permission) => String(permission.user_email || permission.email || '').trim().toLowerCase())
            .filter(Boolean)
        )
      ];

      await base44.entities.Member.update(member.id, {
        member_card_requested_at: nowIso,
        member_card_request_note: missingText,
      });

      if (adminEmails.length > 0) {
        await Promise.allSettled(
          adminEmails.map((email) =>
            deliverMemberNotification({
              recipients: [{ member_email: email }],
              title: 'Solicitação de Cartão de Membro',
              message,
              type: 'member_card_request',
              referenceId: member.id,
            })
          )
        );

        await Promise.allSettled(
          adminEmails.map((email) =>
            base44.integrations.Core.SendEmail({
              to: email,
              subject: 'Solicitação de Cartão de Membro',
              body: `<p>${message.replace(/\n/g, '<br/>')}</p>`,
            })
          )
        );
      }

      return { adminCount: adminEmails.length };
    },
    onSuccess: ({ adminCount }) => {
      queryClient.invalidateQueries({ queryKey: ['memberData'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
      if (adminCount > 0) {
        toast.success('Solicitação enviada ao administrador.');
      } else {
        toast.success('Solicitação registrada. Avise a administração para liberar seu cartão.');
      }
    },
    onError: (error) => {
      toast.error(error?.message || 'Não foi possível enviar a solicitação agora.');
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-slate-50 pb-24">
        <div className="bg-gradient-to-br from-slate-800 to-indigo-900 text-white pt-8 pb-6 px-4">
          <div className="max-w-lg mx-auto">
            <PageHeader title="Cartão de Membro" showBack={true} />
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 py-10">
          <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
            <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-slate-500" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Cartão ainda não liberado</h2>
            <p className="text-sm text-slate-600 mt-2">
              Seu cartão de membro ficará disponível aqui assim que o administrador liberar o acesso.
            </p>

            <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900">Confira seu perfil antes de solicitar</p>
                  <p className="text-sm text-amber-800 mt-1">
                    Preencha corretamente todos os dados do seu perfil, principalmente foto, nascimento, batismo e data de membresia.
                  </p>
                </div>
              </div>
              <div className="mt-3 space-y-1.5">
                {missingFields.length > 0 ? (
                  missingFields.map((field) => (
                    <div key={field} className="flex items-center gap-2 text-sm text-amber-900">
                      <AlertCircle className="w-4 h-4" />
                      <span>{field}</span>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center gap-2 text-sm text-emerald-800">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Perfil com os principais dados preenchidos.</span>
                  </div>
                )}
              </div>
            </div>

            <Button
              type="button"
              className="w-full mt-5 bg-indigo-600 hover:bg-indigo-700"
              onClick={() => requestAccessMutation.mutate()}
              disabled={requestAccessMutation.isPending || !!member?.member_card_requested_at}
            >
              <Send className="w-4 h-4 mr-2" />
              {member?.member_card_requested_at
                ? 'Solicitação já enviada'
                : requestAccessMutation.isPending
                  ? 'Enviando...'
                  : 'Solicitar acesso ao cartão'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      <div className="bg-gradient-to-br from-slate-900 to-indigo-900 text-white pt-8 pb-8 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Cartão de Membro" subtitle="Identificação digital" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5 pb-10">
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
          <ShieldCheck className="w-4 h-4 flex-shrink-0" />
          <span>Cartão liberado pela administração.</span>
        </div>

        <div className="mbv-member-card-pair">
          <div>
            <div className="flex items-center gap-2 mb-3 text-slate-700">
              <CreditCard className="w-4 h-4" />
              <span className="font-semibold">Frente</span>
            </div>
            <MemberCardFront member={member} churchInfo={churchInfo} />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3 text-slate-700">
              <CreditCard className="w-4 h-4" />
              <span className="font-semibold">Verso</span>
            </div>
            <MemberCardBack member={member} churchInfo={churchInfo} />
          </div>
        </div>
      </div>
    </div>
  );
}
